import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FileSpreadsheet, Handshake } from "lucide-react";
import SearchConsole from "../components/search/SearchConsole";
import TableFilterBar from "../components/results/TableFilterBar";
import LicensingResultsList from "../components/results/LicensingResultsList";
import Pagination from "../components/results/Pagination";
import ImageModal from "../components/results/ImageModal";
import TrademarkDetailModal from "../components/results/TrademarkDetailModal";
import Button from "../components/ui/Button";
import { Chip } from "../components/ui/Badge";
import { Alert, EmptyState, SearchingState } from "../components/ui/Feedback";
import { trademarkApi } from "../api/trademarkApi";
import useResultsTable from "../hooks/useResultsTable";
import useLogoSearch from "../hooks/useLogoSearch";
import useTrademarkDetails, { mergeDetail } from "../hooks/useTrademarkDetails";
import { exportResultsToExcel } from "../lib/exportExcel";
import { formatDuration, officeLabel } from "../lib/format";
import { parseSearchedClasses } from "../lib/licensing";

const NO_ROWS = [];
const NO_CONFLICTS = [];

/**
 * A second, independent search surface for a different question than the
 * main clearance search asks. Clearance search asks "would this conflict
 * with something that already exists?" — this asks "who already holds a
 * mark like this, and could they be approached for a license instead of
 * fighting over the name?". Same register, same data, different purpose,
 * so it keeps its own local search state rather than sharing SearchProvider
 * (and its persisted session) with the clearance flow.
 *
 * There's no AI conflict scoring here — that's a clearance-specific signal
 * and doesn't mean anything in a licensing context. The results view is
 * also deliberately its own component (LicensingResultsList), not the
 * clearance search's table/list/grid, because this page answers a
 * different question: for each owner, which of *their* classes overlap
 * with what was searched (why they're a licensing candidate at all), and
 * how to start a conversation with them. The register itself has no
 * email, phone or website for an owner — only a correspondence address —
 * so "contact" here is an outreach search shortcut, clearly labelled as
 * one, not a claim to have found verified contact details.
 *
 * Can be reached two ways: directly from the sidebar (a blank search), or
 * from a "Potential licensing" action on a specific result elsewhere in
 * the app, which arrives with that mark's name/classes/jurisdiction in
 * router state and auto-runs the search immediately.
 */
export default function PotentialLicensingPage() {
  const location = useLocation();
  const prefill = location.state;

  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastQuery, setLastQuery] = useState(prefill ? { ...prefill } : null);
  const [modalImage, setModalImage] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [exportNotice, setExportNotice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const logo = useLogoSearch();

  async function runSearch(payload) {
    setStatus("loading");
    setError(null);
    setLastQuery(payload);
    try {
      const result = await trademarkApi.search(payload);
      setData(result);
      setStatus("ready");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }

  // Arrived from a result row elsewhere — run that mark's search right away.
  useEffect(() => {
    if (prefill?.wordMark)
      runSearch({
        wordMark: prefill.wordMark,
        niceClasses: prefill.niceClasses,
        jurisdiction: prefill.jurisdiction,
      });
    // Only on arrival — re-navigating here with new state is a fresh mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const results = data?.results || NO_ROWS;
  const table = useResultsTable(results, NO_CONFLICTS);
  const registry = useTrademarkDetails();
  const {
    details,
    failed,
    request: requestDetails,
    retry: retryDetail,
    getLoaded,
    isPending,
  } = registry;

  useEffect(() => {
    requestDetails(table.pageRows);
  }, [table.pageRows, requestDetails]);

  const shownRows = useMemo(
    () =>
      table.pageRows.map((row) =>
        mergeDetail(row, details[row.applicationNumber]),
      ),
    [table.pageRows, details],
  );

  const orderedRows = table.filteredRows;
  const openIndex =
    openId === null
      ? -1
      : orderedRows.findIndex((r) => r.trademarkId === openId);
  const baseRow = openIndex >= 0 ? orderedRows[openIndex] : null;
  const openRow = baseRow
    ? mergeDetail(baseRow, details[baseRow.applicationNumber])
    : null;

  useEffect(() => {
    if (!baseRow) return;
    const neighbours = [
      orderedRows[openIndex - 1],
      orderedRows[openIndex + 1],
    ].filter((row) => row && !failed[row.applicationNumber]);
    requestDetails([baseRow, ...neighbours]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRow, openIndex, orderedRows, requestDetails]);

  function detailStateFor(row) {
    if (!row) return "none";
    if (row.source !== "euipo") return "unsupported";
    if (!row.applicationNumber) return "none";
    if (details[row.applicationNumber]) return "loaded";
    if (failed[row.applicationNumber]) return "failed";
    return "loading";
  }

  async function handleExport(scope) {
    if (exporting) return;
    const rowsByScope = {
      selected: table.filteredRows.filter((r) =>
        table.selected.has(r.trademarkId),
      ),
      filtered: table.filteredRows,
      all: results,
    };
    const scopeLabel = {
      selected: "Rows selected in the table",
      filtered: "Rows left after the table filters",
      all: "Every row the search returned",
    };
    const baseRows = rowsByScope[scope];
    const wantsRecord = (row) =>
      row.source === "euipo" && row.applicationNumber;

    setExporting(true);
    try {
      const missing = baseRows.filter(
        (row) => wantsRecord(row) && !getLoaded()[row.applicationNumber],
      );
      if (missing.length) {
        setExportNotice(
          `Collecting the full record for ${missing.length} mark(s)…`,
        );
        await requestDetails(missing);
      }
      const loaded = getLoaded();
      const rows = baseRows.map((row) =>
        mergeDetail(row, loaded[row.applicationNumber]),
      );
      const outcome = exportResultsToExcel({
        rows,
        similarityMap: table.similarityMap,
        reasoningMap: table.reasoningMap,
        query: data.query,
        source: data.source,
        resultsCount: results.length,
        filters: scope === "all" ? null : table.filters,
        scope: scopeLabel[scope],
      });
      setExportNotice(
        outcome.ok
          ? `Saved ${outcome.count} ${outcome.count === 1 ? "row" : "rows"} to ${outcome.filename}.`
          : outcome.reason,
      );
    } finally {
      setExporting(false);
      setTimeout(() => setExportNotice(null), 8000);
    }
  }

  const noRowsAtAll = status === "ready" && results.length === 0;
  const filteredToNothing =
    !noRowsAtAll && results.length > 0 && table.filteredRows.length === 0;
  const searchedClasses = useMemo(
    () => parseSearchedClasses(data?.query),
    [data?.query],
  );

  const viewProps = {
    rows: shownRows,
    selected: table.selected,
    onToggleRow: table.toggleRow,
    onOpenImage: setModalImage,
    onOpenDetail: (row) => setOpenId(row.trademarkId),
    isDetailLoading: isPending,
    searchedClasses,
  };

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded bg-[linear-gradient(135deg,#8f5eeb,#7a54e5)]">
          <Handshake
            className="h-4 w-4 text-white"
            strokeWidth={2.25}
            aria-hidden="true"
          />
        </span>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            Potential licensing
          </h1>
          <p className="text-sm text-ink-600">
            Find who already holds a mark like this, so it can be approached for
            a license instead of contested.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <SearchConsole
          onSearch={(payload) => {
            logo.clear();
            return runSearch(payload);
          }}
          onLogoSearch={(args) => logo.searchByLogo(args, runSearch)}
          loading={status === "loading" || logo.reading}
          initialQuery={lastQuery}
        />
      </div>

      {logo.error && (
        <div className="mt-4">
          <Alert tone="error" title="The logo couldn't be searched">
            {logo.error}
          </Alert>
        </div>
      )}

      {logo.notice && (
        <div className="mt-4">
          <Alert tone="info" title="Logo read">
            {logo.notice}
          </Alert>
        </div>
      )}

      {status === "loading" && (
        <div className="mt-6">
          <SearchingState />
        </div>
      )}

      {status === "error" && error && (
        <div className="mt-6">
          <Alert tone="error" title="The search didn't run">
            {error}
          </Alert>
        </div>
      )}

      {exportNotice && (
        <div className="mt-4">
          <Alert tone="info">{exportNotice}</Alert>
        </div>
      )}

      {noRowsAtAll && (
        <div className="mt-6">
          <EmptyState title="Nothing in the register matched that">
            No record came back for this query. Try a shorter or differently
            spelled term, or widen the jurisdiction and class filters.
          </EmptyState>
        </div>
      )}

      {status === "ready" && results.length > 0 && (
        <div className="mt-6">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Chip>{results.length} records</Chip>
            <Chip>From {(data.source || "").toUpperCase()}</Chip>
            {data.durationMs ? (
              <Chip>{formatDuration(data.durationMs)}</Chip>
            ) : null}
            {data.query?.jurisdiction && (
              <Chip>{officeLabel(data.query.jurisdiction)}</Chip>
            )}
          </div>

          <TableFilterBar
            filters={table.filters}
            onChange={table.setFilters}
            onReset={table.resetFilters}
            options={table.options}
            activeCount={table.activeFilterCount}
            hasSimilarity={false}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 border-x border-ink-200 bg-paper px-4 py-2.5">
            <div className="flex items-center gap-3">
              {table.selected.size > 0 ? (
                <>
                  <span className="text-xs font-semibold text-ink-900">
                    {table.selected.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={table.clearSelection}
                    className="text-xs font-medium text-ink-600 underline underline-offset-2 hover:text-ink-900"
                  >
                    Clear
                  </button>
                </>
              ) : (
                <span className="text-xs text-ink-600">
                  {table.filteredRows.length === results.length
                    ? `${results.length} records`
                    : `${table.filteredRows.length} of ${results.length} records`}
                </span>
              )}
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={FileSpreadsheet}
              onClick={() =>
                handleExport(table.selected.size > 0 ? "selected" : "filtered")
              }
              loading={exporting}
            >
              Export to Excel
            </Button>
          </div>

          {filteredToNothing ? (
            <div className="rounded-b-soft border border-ink-200 bg-white px-6 py-16 text-center">
              <p className="font-display text-lg text-ink-900">
                No rows match these filters
              </p>
              <p className="mx-auto mt-1.5 max-w-reading text-sm text-ink-600">
                All {results.length} records are still loaded — clear a filter
                to bring them back.
              </p>
              <div className="mt-5 flex justify-center">
                <Button variant="secondary" onClick={table.resetFilters}>
                  Clear filters
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-b-soft border-b border-ink-200">
              <LicensingResultsList {...viewProps} />

              <div className="border-x border-ink-200">
                <Pagination
                  page={table.page}
                  pageSize={table.pageSize}
                  total={table.filteredRows.length}
                  onPageChange={table.setPage}
                  onPageSizeChange={table.setPageSize}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {modalImage && (
        <ImageModal
          imageUrl={modalImage.url}
          wordMark={modalImage.wordMark}
          onClose={() => setModalImage(null)}
        />
      )}

      {openRow && (
        <TrademarkDetailModal
          row={openRow}
          detailState={detailStateFor(openRow)}
          detailError={failed[openRow.applicationNumber]}
          onRetry={() => retryDetail(baseRow)}
          position={{ index: openIndex + 1, total: orderedRows.length }}
          onPrev={
            openIndex > 0
              ? () => setOpenId(orderedRows[openIndex - 1].trademarkId)
              : undefined
          }
          onNext={
            openIndex < orderedRows.length - 1
              ? () => setOpenId(orderedRows[openIndex + 1].trademarkId)
              : undefined
          }
          onClose={() => setOpenId(null)}
          onOpenImage={setModalImage}
        />
      )}
    </div>
  );
}
